import { useCallback, useEffect, useRef, useState } from 'react';
import { saveBlob } from '@/lib/download';
import { Loader2 } from 'lucide-react';
import { useFullscreen } from '@/hooks/useFullscreen';
import { cn } from '@/lib/utils';
import { FRAME_HTML } from './frame';
import { isFromFrame, pdfJsSources, type ToFrame, type ZoomMode } from './protocol';

/**
 * What the header's document tools drive (StudyHeader), handed up by the view
 * while a document is open: where the reader is, and what it can be asked.
 */
export interface DocumentControls {
  page: number;
  pages: number;
  /** Where the zoom is: at the whole page (its floor), at the width, or null anywhere else. */
  fit: 'width' | 'page' | null;
  /** The zoom as a percentage of the width fit: 100 fills the width, less shows more of the page, more goes closer. */
  percent: number;
  /** As far in as the zoom goes, the same way (where a page's canvas would outgrow the browser). */
  max: number;
  goto: (page: number) => void;
  zoom: (mode: ZoomMode) => void;
  /** A share of the way from the whole page (0) to the width (1): the digit ladder. */
  zoomBetween: (share: number) => void;
  download: () => void;
}

/**
 * A document (a PDF image of the case) where a slide's canvas would be: the
 * pages in a scrolling column, drawn by pdf.js inside a sandboxed frame
 * (frame.ts). The page fetches the PDF with its own session and hands the
 * bytes to the frame, which has no origin and cannot ask for anything itself.
 * The reader's controls — the page, the zoom, the fit, a download — are the
 * header's, like a slide's tools (`onControls`), and its keys are a slide's
 * (Viewport.tsx): + and − zoom, 1 is the whole page, 0 the width, 2–9 the
 * rungs between, f fills the screen. The zoom goes on past the width, as far
 * as a page's canvas allows.
 *
 * `onReady` fires when the first page has been drawn (the image is seen, as a
 * slide is when its viewer is up). `onFullscreenToggleReady` hands the header
 * a way to fill the screen with the document, as the Viewport does.
 */
interface Props {
  /** `GET /images/:id/document`, under the page's API base; null for a document the page cannot ask for. */
  url: string | null;
  /** The image's name, for the file a download is saved under. */
  name: string | null;
  onReady?: () => void;
  onFullscreenToggleReady?: (toggle: (() => void) | null) => void;
  /** The controls while the document is open; null while it loads, fails or goes. */
  onControls?: (controls: DocumentControls | null) => void;
  className?: string;
}

type Status = { state: 'loading' } | { state: 'open' } | { state: 'error'; message: string };

export function DocumentView({ url, name, onReady, onFullscreenToggleReady, onControls, className }: Props) {
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [status, setStatus] = useState<Status>({ state: 'loading' });
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(1);
  const [fit, setFit] = useState<'width' | 'page' | null>('width');
  const [percent, setPercent] = useState(100);
  const [max, setMax] = useState(Infinity);
  // The three things an open needs, arriving in any order: the frame's hello,
  // pdf.js's sources, the PDF's bytes. Sent once, when the last one lands.
  const frameHello = useRef(false);
  const pdf = useRef<ArrayBuffer | null>(null);
  const sources = useRef<{ lib: string; worker: string } | null>(null);
  const sent = useRef(false);
  // Each document's open has its own token; the frame says it back in every
  // word about that document, and a word about the one before is let go.
  const token = useRef(0);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  const send = useCallback((m: ToFrame, transfer?: Transferable[]) => {
    frameRef.current?.contentWindow?.postMessage(m, '*', transfer ?? []);
  }, []);

  // The colours around the document, so the frame matches the page's theme.
  const themeOf = useCallback((): { bg?: string; fg?: string } => {
    const host = hostRef.current;
    if (!host) return {};
    const style = getComputedStyle(host);
    return { bg: style.backgroundColor, fg: style.color };
  }, []);

  const openIfReady = useCallback(() => {
    if (sent.current || !frameHello.current || !pdf.current || !sources.current) return;
    sent.current = true;
    const bytes = pdf.current;
    pdf.current = null;
    send({ type: 'open', token: token.current, pdf: bytes, lib: sources.current.lib, worker: sources.current.worker, ...themeOf() }, [bytes]);
  }, [send, themeOf]);

  // The PDF and pdf.js, fetched side by side; a change of document starts over.
  useEffect(() => {
    sent.current = false;
    pdf.current = null;
    token.current += 1;
    setStatus({ state: 'loading' });
    setPages(0);
    setPage(1);
    setFit('width');
    setPercent(100);
    setMax(Infinity);
    if (!url) {
      setStatus({ state: 'error', message: 'This document is not available here.' });
      return;
    }
    const controller = new AbortController();
    (async () => {
      try {
        const [res, src] = await Promise.all([fetch(url, { credentials: 'include', signal: controller.signal }), pdfJsSources()]);
        if (!res.ok) throw new Error(res.status === 404 ? 'This document is not available.' : `The document could not be loaded (${res.status}).`);
        const bytes = await res.arrayBuffer();
        if (controller.signal.aborted) return;
        pdf.current = bytes;
        sources.current = src;
        openIfReady();
      } catch (err) {
        if (controller.signal.aborted) return;
        setStatus({ state: 'error', message: err instanceof Error ? err.message : 'The document could not be loaded.' });
      }
    })();
    return () => controller.abort();
  }, [url, openIfReady]);

  // What the frame says. Only this frame's window is listened to.
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (!frameRef.current || e.source !== frameRef.current.contentWindow || !isFromFrame(e.data)) return;
      const m = e.data;
      // About a document that is no longer the one asked for: let go.
      if ('token' in m && m.token !== token.current) return;
      switch (m.type) {
        case 'hello':
          frameHello.current = true;
          openIfReady();
          break;
        case 'loaded':
          setPages(m.pages);
          setStatus({ state: 'open' });
          break;
        case 'ready':
          onReadyRef.current?.();
          break;
        case 'page':
          setPage(m.page);
          break;
        case 'zoom':
          setFit(m.fit);
          setPercent(m.percent);
          setMax(m.max);
          break;
        case 'error':
          setStatus({ state: 'error', message: m.message });
          break;
        case 'log':
          // The frame's own account of itself: it has no console anyone can open.
          console.info('[document]', m.message);
          break;
        case 'key':
          // The frame has the focus; the page's shortcuts (d, b, ?) still work.
          document.dispatchEvent(new KeyboardEvent('keydown', { key: m.key, code: m.code, ctrlKey: m.ctrlKey, metaKey: m.metaKey, shiftKey: m.shiftKey, altKey: m.altKey, repeat: m.repeat, bubbles: true, cancelable: true }));
          break;
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [openIfReady]);

  // The theme can change under an open document (the account's setting).
  useEffect(() => {
    const observer = new MutationObserver(() => send({ type: 'theme', ...themeOf() }));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'data-theme', 'style'] });
    return () => observer.disconnect();
  }, [send, themeOf]);

  const { ref: fsRef, toggle: toggleFullscreen } = useFullscreen<HTMLDivElement>();
  const fsReadyRef = useRef(onFullscreenToggleReady);
  fsReadyRef.current = onFullscreenToggleReady;
  useEffect(() => {
    fsReadyRef.current?.(toggleFullscreen);
    return () => fsReadyRef.current?.(null);
  }, [toggleFullscreen]);

  // A slide's keys (Viewport.tsx): + and − zoom, 1 is the whole page, 0 the
  // width, 2–9 the rungs between, f fills the screen. Never from a field. The
  // frame reports the keys it does not use as presses on this document, so
  // they work with the focus in the reader too.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      if (e.key === 'f' || e.key === 'F') {
        e.preventDefault();
        void toggleFullscreen();
      } else if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        send({ type: 'zoom', mode: 'in' });
      } else if (e.key === '-' || e.key === '_') {
        e.preventDefault();
        send({ type: 'zoom', mode: 'out' });
      } else if (e.key >= '0' && e.key <= '9') {
        e.preventDefault();
        // A held digit repeats toward the same rung: nothing to gain.
        if (e.repeat) return;
        const d = Number(e.key);
        send({ type: 'zoom', mode: 'between', share: (d === 0 ? 9 : d - 1) / 9 });
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [send, toggleFullscreen]);

  const setHost = useCallback((node: HTMLDivElement | null) => {
    hostRef.current = node;
    fsRef(node);
  }, [fsRef]);

  // The header's tools, handed up while the document is open.
  const open = status.state === 'open';
  const onControlsRef = useRef(onControls);
  onControlsRef.current = onControls;
  useEffect(() => {
    if (!open) {
      onControlsRef.current?.(null);
      return;
    }
    onControlsRef.current?.({
      page,
      pages,
      fit,
      percent,
      max,
      goto: (n: number) => {
        const target = Math.min(pages, Math.max(1, Math.round(n)));
        if (!Number.isFinite(target) || !pages) return;
        send({ type: 'goto', page: target });
      },
      zoom: (mode: ZoomMode) => send({ type: 'zoom', mode }),
      zoomBetween: (share: number) => send({ type: 'zoom', mode: 'between', share }),
      // The bytes were handed to the frame, so they are asked for again (the
      // response is immutable, and the browser keeps it).
      download: () => {
        if (!url) return;
        void (async () => {
          try {
            const res = await fetch(url, { credentials: 'include' });
            if (!res.ok) throw new Error(String(res.status));
            saveBlob(await res.blob(), `${(name ?? '').trim().replace(/\.pdf$/i, '') || 'document'}.pdf`);
          } catch {
            /* the button stays; the person may try again */
          }
        })();
      },
    });
  }, [open, page, pages, fit, percent, max, url, name, send]);
  useEffect(() => () => onControlsRef.current?.(null), []);

  return (
    <div ref={setHost} className={cn('relative h-full w-full bg-viewport text-foreground', className)} data-testid="document-view">
      <iframe
        ref={frameRef}
        title={name ?? 'Document'}
        sandbox="allow-scripts"
        srcDoc={FRAME_HTML}
        className="absolute inset-0 h-full w-full border-0"
      />
      {status.state === 'loading' && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label="Loading the document" />
        </div>
      )}
      {status.state === 'error' && (
        <div className="absolute inset-0 flex items-center justify-center bg-viewport p-6">
          <div className="max-w-sm rounded-md border bg-background p-4 text-center text-sm">
            <div className="font-medium">This document cannot be shown</div>
            <div className="mt-1 text-muted-foreground">{status.message}</div>
          </div>
        </div>
      )}
    </div>
  );
}
